/**
=========================================================
* Material Dashboard 2  React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useMemo } from "react";

// porp-types is a library for typechecking of props
import PropTypes from "prop-types";

// react-chartjs-2 components
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

// @mui material components
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// VerticalBarChart configurations
import configs from "examples/Charts/BarCharts/VerticalBarChart/configs";

// Material Dashboard 2 React base styles
import colors from "assets/theme/base/colors";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

function VerticalBarChart({
  icon,
  title,
  description,
  height,
  chart,
  xAxisTitle,
  yAxisTitle,
  customOptions,
}) {
  const chartDatasets = chart.datasets
    ? chart.datasets.map((dataset) => ({
        ...dataset,
        weight: 5,
        borderWidth: 0,
        borderRadius: 4,
        backgroundColor: "#3A6EA5",
        fill: false,
        maxBarThickness: 35,
      }))
    : [];

  // const { data, options } = configs(chart.labels || [], chartDatasets);
  const { data, options } = configs(chart.labels || [], chartDatasets, xAxisTitle, yAxisTitle);

  const updatedOptions = customOptions
    ? {
        ...options,
        ...customOptions,
        scales: {
          ...options.scales,
          ...customOptions.scales,
          x: {
            ...options.scales.x,
            ...customOptions.scales?.x,
          },
          y: {
            ...options.scales.y,
            ...customOptions.scales?.y,
          },
        },
      }
    : options;
  const renderChart = (
    <MDBox>
      {(title || description) && (
        <MDBox display="flex" alignItems="center" mb={1}>
          {icon.component && (
            <MDBox
              width="3rem"
              height="3rem"
              bgColor={icon.color || "primary"}
              variant="gradient"
              borderRadius="lg"
              display="flex"
              justifyContent="center"
              alignItems="center"
              color="white"
              mr={1.5}
            >
              <Icon fontSize="medium">{icon.component}</Icon>
            </MDBox>
          )}
          <MDBox>
            {title && <MDTypography variant="h6">{title}</MDTypography>}
            {description && (
              <MDTypography variant="button" color="text" mt={0.5}>
                {description}
              </MDTypography>
            )}
          </MDBox>
        </MDBox>
      )}

      {useMemo(
        () => (
          <MDBox sx={{ height: "100%", maxHeight: 260 }}>
            {" "}
            <Bar data={data} options={updatedOptions} redraw />
          </MDBox>
        ),
        [chart, height]
      )}
    </MDBox>
  );

  return title || description ? <Card>{renderChart}</Card> : renderChart;
}

// Setting default values for the props of VerticalBarChart
VerticalBarChart.defaultProps = {
  icon: { color: "info", component: "" },
  title: "",
  description: "",
  height: 220,
  xAxisTitle: "Result %", // Default x-axis title
  yAxisTitle: "# Employees", // Default y-axis title
  customOptions: PropTypes.object,
};

// Typechecking props for the VerticalBarChart
VerticalBarChart.propTypes = {
  icon: PropTypes.shape({
    color: PropTypes.oneOf([
      "primary",
      "secondary",
      "info",
      "success",
      "warning",
      "error",
      "light",
      "dark",
    ]),
    component: PropTypes.node,
  }),
  title: PropTypes.string,
  description: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  chart: PropTypes.objectOf(PropTypes.array).isRequired,
  xAxisTitle: PropTypes.string, // New prop for x-axis title
  yAxisTitle: PropTypes.string, // New prop for y-axis title
  customOptions: PropTypes.object,
};

export default VerticalBarChart;
